/**
 * Slideshow Text Animation Controller
 * 
 * Handles slide-in text animations for slideshow content.
 * Listens for slide change events and manages animation state to ensure
 * animations replay when slides are revisited.
 */

import { SlideshowSelectEvent } from '@theme/events';

/**
 * Initializes text animation handling for a slideshow component
 * @param {HTMLElement} slideshowComponent - The slideshow-component element
 */
function initSlideshowTextAnimation(slideshowComponent) {
  if (!slideshowComponent) return;

  // Listen for slide change events
  slideshowComponent.addEventListener(SlideshowSelectEvent.eventName, (event) => {
    const { slide, previousIndex } = event.detail;
    const slides = slideshowComponent.querySelectorAll('slideshow-slide');
    
    // Get the previous slide element
    const previousSlide = slides[previousIndex];
    
    // Reset animations on the previous slide to prepare for replay
    if (previousSlide) {
      resetSlideAnimations(previousSlide);
    }
    
    // Trigger animations on the new active slide
    if (slide) {
      triggerSlideAnimations(slide);
    }
  });

  // Initialize first slide animations on load
  const initialSlide = slideshowComponent.querySelector('slideshow-slide[aria-hidden="false"]');
  if (initialSlide) {
    triggerSlideAnimations(initialSlide);
  }
}

/**
 * Resets animations on a slide by forcing a reflow
 * This ensures animations will replay when the slide becomes active again
 * @param {HTMLElement} slide - The slideshow-slide element
 */
function resetSlideAnimations(slide) {
  const contentElements = slide.querySelectorAll('.slide__content .group-block-content > *');
  
  contentElements.forEach((element) => {
    // Remove animation by setting it to none temporarily
    element.style.animation = 'none';
    
    // Force reflow to reset the animation state
    // Using void to suppress the "unused expression" warning
    void element.offsetHeight;
    
    // Remove the inline style to allow CSS animations to take over again
    element.style.animation = '';
  });
}

/**
 * Triggers animations on the active slide
 * @param {HTMLElement} slide - The slideshow-slide element
 */
function triggerSlideAnimations(slide) {
  const contentElements = slide.querySelectorAll('.slide__content .group-block-content > *');
  
  // Reset first to ensure clean animation state
  contentElements.forEach((element, index) => {
    // Set CSS custom property for staggered animations on 4th+ elements
    if (index >= 3) {
      element.style.setProperty('--child-index', index - 3);
    }
    
    // Force animation restart
    element.style.animation = 'none';
    void element.offsetHeight;
    element.style.animation = '';
  });
}

// Initialize on DOM ready
function init() {
  // Find all slideshow components and initialize animation handling
  const slideshowComponents = document.querySelectorAll('slideshow-component');
  
  slideshowComponents.forEach((slideshow) => {
    initSlideshowTextAnimation(slideshow);
  });
}

// Handle both initial load and dynamic content (e.g., Shopify theme editor)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Re-initialize on Shopify theme editor section reload
document.addEventListener('shopify:section:load', (event) => {
  const slideshowComponents = event.target.querySelectorAll('slideshow-component');
  slideshowComponents.forEach((slideshow) => {
    initSlideshowTextAnimation(slideshow);
  });
});

export { initSlideshowTextAnimation, resetSlideAnimations, triggerSlideAnimations };

